import type {
  ConnectionType,
  GETConnectionMetadata,
} from '~/services/apiClient'
import { PortComplex } from '~/services/apiClient'

/**
 * Frontend category type. Mirrors the backend `ConnectionCategory` enum values
 * (including `appointment_system`, which the backend now serves). Components use
 * this alias rather than importing `ConnectionCategory` from the API client
 * directly.
 */
export type ConnectionCategoryType =
  | 'terminal_portal'
  | 'shipping_line_portal'
  | 'appointment_system'
  | 'gps'
  | 'tms'
  | 'other'

// Port-complex region. Used by the region filter on the settings page.
// UTAS-only / TMS / generic integrations don't carry a region.
export type ConnectionRegion = 'lalb' | 'oakland'

export interface ConnectionMetadata {
  type: ConnectionType
  label: string
  url: string
  note?: string
  // Help Center article on setting this connection up. Only some have one.
  helpUrl?: string
  category: ConnectionCategoryType
  // The port complexes this connection applies to. A connection can span more
  // than one (e.g. Emodal covers both LA/LB and Oakland), so this is a list.
  regions?: ConnectionRegion[]
  // `managed` connections are provisioned by Dray Dog on the user's behalf
  // (e.g. LBCT API). They render as read-only status rows — no credential
  // inputs, no actions — and are excluded from the user-action counts on the
  // page (Failing tile, error callout, "needs attention" pinned section).
  managed?: boolean
  // When a saved connection of this type is Validated, this row is not setup
  // work. Settings All, Paused, and Not used still show it.
  setupCoveredBy?: ConnectionType
}

const PORT_COMPLEX_TO_REGION: Record<PortComplex, ConnectionRegion> = {
  [PortComplex.LaLb]: 'lalb',
  [PortComplex.Oak]: 'oakland',
}

/**
 * Adapts the backend `GETConnectionMetadata` (from `/connections/editable_types/`)
 * into the frontend `ConnectionMetadata` shape consumed by the settings UI. This
 * is the single boundary that depends on the generated wire type — everything
 * downstream types against `ConnectionMetadata`.
 */
export function connectionMetadataFromGET(
  meta: GETConnectionMetadata
): ConnectionMetadata {
  return {
    type: meta.connection_type,
    label: meta.label ?? '',
    url: meta.url ?? '',
    note: meta.note,
    helpUrl: meta.help_url,
    category: meta.connection_category,
    regions: meta.region?.map((pc) => PORT_COMPLEX_TO_REGION[pc]),
    managed: meta.is_dray_dog_managed,
    setupCoveredBy: meta.setup_covered_by,
  }
}
